<template>
  <div class="card">
    <a-page-header
      style="border: 1px solid rgb(235, 237, 240)"
      :title="classroom.name"
      :sub-title="$t('inscription.nbEleves', { number: classroomCount })"
      class="card-header"
      @back="() => $router.push('/inscriptionScolarite')"
    >
      <template slot="extra">
        <a-tooltip placement="left" v-if="classroom._id">
          <template slot="title">
            {{
              classroom.enrollmentConfirmed
                ? $t("inscription.enrollmentVisibleTooltip", {
                    name: classroom.name,
                  })
                : $t("inscription.enrollmentHiddenTooltip", {
                    name: classroom.name,
                  })
            }}
          </template>
          <a-button
            :type="classroom.enrollmentConfirmed ? 'default' : 'danger'"
            :class="
              classroom.enrollmentConfirmed ? 'enrollment-availability-btn' : ''
            "
            :loading="enrollmentConfirmationLoading"
            :disabled="enrollmentConfirmationLoading"
            @click="confirmEnrollmentValidation"
          >
            {{
              classroom.enrollmentConfirmed
                ? $t("inscription.enrollmentVisibleTag")
                : $t("inscription.enrollmentHiddenTag")
            }}
          </a-button>
        </a-tooltip>
      </template>
    </a-page-header>
    <div class="card-body">
      <h5 style="color: blue; font-weight: bold">
        {{ $t("inscription.inscription") }}
      </h5>
      <div style="margin-bottom: 20px">
        <a-button
          @click="showModal"
          :disabled="classroom.status !== 'active'"
          type="user-add"
          ><a-icon type="user-add" />{{
            $t("inscription.nouvInscri")
          }}</a-button
        >
        <!-- When countryIsTN is true, show dropdowns -->
        <a-button-group
          style="float: right; margin-right: 10px"
          v-if="countryIsTN"
        >
          <!-- Dropdown for Fiche Parent Classe -->
          <a-dropdown>
            <a-button type="primary">
              <a-icon type="download" />
              {{ $t("inscription.ficheParentClasse") }}
              <a-icon type="down" />
            </a-button>
            <a-menu slot="overlay">
              <a-menu-item @click="openParentSheetModal('pdf', 'fr')">
                <a-icon type="file-pdf" /> PDF - {{ $t("language.fr") }}
              </a-menu-item>
              <a-menu-item @click="openParentSheetModal('pdfAr')">
                <a-icon type="file-pdf" /> PDF - {{ $t("language.ar") }}
              </a-menu-item>
              <a-menu-item @click="openParentSheetModal('excel', 'fr')">
                <a-icon type="table" /> Excel - {{ $t("language.fr") }}
              </a-menu-item>
              <a-menu-item @click="openParentSheetModal('excel', 'ar')">
                <a-icon type="table" /> Excel - {{ $t("language.ar") }}
              </a-menu-item>
            </a-menu>
          </a-dropdown>

          <!-- Dropdown for Fiche Classe -->
          <a-dropdown>
            <a-button type="primary">
              <a-icon type="file-pdf" />
              {{ $t("inscription.ficheClasse") }}
              <a-icon type="down" />
            </a-button>
            <a-menu slot="overlay">
              <a-menu-item @click="generateFicheClassePDF()">{{
                $t("language.fr")
              }}</a-menu-item>
              <a-menu-item @click="generateFicheClassePDFar()">{{
                $t("language.ar")
              }}</a-menu-item>
            </a-menu>
          </a-dropdown>
        </a-button-group>

        <!-- When countryIsTN is false, show dropdowns -->
        <a-button-group
          style="float: right; margin-right: 10px"
          v-if="!countryIsTN"
        >
          <!-- Dropdown for Fiche Parent Classe -->
          <a-dropdown>
            <a-button type="primary">
              <a-icon type="download" />
              {{ $t("inscription.ficheParentClasse") }}
              <a-icon type="down" />
            </a-button>
            <a-menu slot="overlay">
              <a-menu-item @click="openParentSheetModal('pdf')">
                <a-icon type="file-pdf" /> PDF
              </a-menu-item>
              <a-menu-item @click="openParentSheetModal('excel')">
                <a-icon type="table" /> Excel
              </a-menu-item>
            </a-menu>
          </a-dropdown>

          <!-- Normal button for Fiche Classe -->
          <a-button type="primary" @click="generateFicheClassePDF()">
            <a-icon type="file-pdf" />
            {{ $t("inscription.ficheClasse") }}
          </a-button>
        </a-button-group>
      </div>
      <a-table
        @change="tableChanged"
        :pagination="true"
        :data-source="data"
        :columns="columns"
        :loading="tableLoading"
      >
        <template slot="gender" slot-scope="text">
          {{ text == "male" ? $t("profil.garcon") : $t("profil.fille") }}
        </template>
        <div
          slot="filterDropdown"
          slot-scope="{
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
            column,
          }"
          style="padding: 8px"
        >
          <a-input
            v-ant-ref="(c) => (searchInput = c)"
            :placeholder="`${$t('personnel.chercher')} ${column.title}`"
            :value="selectedKeys[0]"
            style="width: 188px; margin-bottom: 8px; display: block"
            @change="
              (e) => setSelectedKeys(e.target.value ? [e.target.value] : [])
            "
            @pressEnter="
              () => handleSearch(selectedKeys, confirm, column.dataIndex)
            "
          />
          <a-button
            type="primary"
            icon="search"
            size="small"
            style="width: 90px; margin-right: 8px"
            @click="() => handleSearch(selectedKeys, confirm, column.dataIndex)"
            >{{ $t("recette.chercher") }}</a-button
          >
          <a-button
            size="small"
            style="width: 90px"
            @click="() => handleReset(clearFilters)"
            >{{ $t("action.reinitialiser") }}</a-button
          >
        </div>
        <a-icon
          slot="filterIcon"
          slot-scope="filtered"
          type="search"
          :style="{ color: filtered ? '#108ee9' : undefined }"
        />
        <template slot="fullName" slot-scope="text, record">
          <router-link :to="`/eleve/${record._id}`" class="kit__utils__link">{{
            record.fullName
          }}</router-link>
        </template>
        <template slot="parent" slot-scope="text, record">
          <div
            v-if="
              record.parent && (record.parent.father || record.parent.mother)
            "
          >
            <span v-if="record.parent.father && record.parent.mother">
              {{ record.parent.father }} / {{ record.parent.mother }}
            </span>
            <span v-else-if="record.parent.father">
              {{ record.parent.father }}
            </span>
            <span v-else-if="record.parent.mother">
              {{ record.parent.mother }}
            </span>
          </div>
          <span v-else>--</span>
        </template>
        <template slot="phone" slot-scope="text, record">
          <div
            v-if="record.phone && (record.phone.father || record.phone.mother)"
          >
            <span v-if="record.phone.father && record.phone.mother" dir="ltr">
              {{ record.phone.father }} / {{ record.phone.mother }}
            </span>
            <span v-else-if="record.phone.father" dir="ltr">
              {{ record.phone.father }}
            </span>
            <span v-else-if="record.phone.mother" dir="ltr">
              {{ record.phone.mother }}
            </span>
          </div>
          <span v-else>--</span>
        </template>
        <template slot="customRender" slot-scope="text, record, index, column">
          <span v-if="searchText && searchedColumn === column.dataIndex">
            <template
              v-for="(fragment, i) in text
                .toString()
                .split(new RegExp(`(?<=${searchText})|(?=${searchText})`, 'i'))"
            >
              <mark
                v-if="fragment.toLowerCase() === searchText.toLowerCase()"
                :key="i"
                class="highlight"
                >{{ fragment }}</mark
              >
              <template v-else>{{ fragment }}</template>
            </template>
          </span>
          <template v-else>{{ text }}</template>
        </template>

        <a-select
          slot="changeClassroom"
          slot-scope="text, record"
          show-search
          option-filter-prop="children"
          :disabled="changeClassRoomDisable"
          :loading="changeClassRoomDisable"
          :filter-option="filterOption"
          :placeholder="$t('liste.classes')"
          @change="(value) => changeClassRoom(value, record)"
          :default-value="classId ? classId : ''"
          class="w-100"
        >
          <a-select-opt-group
            v-for="group in classRoomsGroupedByLevel"
            :key="group.key"
            :label="group.label"
          >
            <a-select-option
              :value="item._id"
              v-for="item in group.classrooms"
              :key="item._id"
              :disabled="item.status == 'inactive'"
            >
              {{ item.name }}
              <a-tag
                v-if="item.classroomType === 'affectation'"
                color="orange"
                class="ml-2"
              >
                {{ $t("etablissement.niveauAffectation") }}
              </a-tag>
            </a-select-option>
          </a-select-opt-group>
        </a-select>
        <a-select
          slot="changeGroup"
          slot-scope="text, record"
          show-search
          option-filter-prop="children"
          :disabled="changeGroupDisable"
          :loading="changeGroupDisable"
          :filter-option="filterOption"
          :placeholder="$t('all.nonGroupAssigned')"
          @change="(value) => changeGroup(value, record)"
          :value="record.groupName || ''"
          class="w-100"
        >
          <a-select-option :value="''">{{
            $t("all.nonGroupAssigned")
          }}</a-select-option>
          <a-select-option :value="'1'"
            >{{ $t("sms.groupe") }} 1</a-select-option
          >
          <a-select-option :value="'2'"
            >{{ $t("sms.groupe") }} 2</a-select-option
          >
        </a-select>
        <template slot="actions" slot-scope="text, record">
          <a-button
            type="primary"
            size="small"
            :loading="loadingSave"
            @click="editScolariteFees(record)"
          >
            <a-icon type="edit" />{{ $t("all.edit") }}
          </a-button>
        </template>
      </a-table>
      <div>
        <a-modal
          v-model="visibleModal"
          @cancel="handleCancel"
          :title="$t('all.enterInformationBelow')"
          :width="750"
          :height="1200"
          :footer="false"
        >
          <a-form :form="form" class="container" @submit="addInscription">
            <div class="row">
              <div class="col-lg-6">
                <a-form-item :label="$t('inscription.enfant')">
                  <a-select
                    show-search
                    :autoFocus="true"
                    option-filter-prop="children"
                    mode="multiple"
                    :filter-option="filterOption"
                    :placeholder="$t('inscription.enfant')"
                    v-decorator="[
                      'students',
                      {
                        rules: [
                          {
                            required: true,
                            message: $t('requis.selectionEnfant'),
                          },
                        ],
                      },
                    ]"
                  >
                    <a-select-option
                      v-for="eleve in eleves"
                      :key="eleve._id"
                      :value="eleve._id"
                    >
                      {{ eleve.firstName }}{{ eleve.middleName ? " " + eleve.middleName : "" }} {{ eleve.lastName }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </div>
              <div class="cold-lg-6">
                <a-form-item :label="$t('all.startDate')">
                  <a-date-picker
                    format="DD/MM/YYYY"
                    :disabled-date="disabledDate"
                    @change="
                      (val) => {
                        setIncludedFees(val);
                      }
                    "
                    v-decorator="[
                      'start',
                      {
                        rules: [
                          {
                            required: false,
                          },
                        ],
                      },
                    ]"
                  />
                </a-form-item>
              </div>
            </div>
            <div class="row"></div>
            <div class="row">
              <a-divider>{{ $t("paiement.paymentMode") }}</a-divider>

              <div class="col-lg-3">
                <h6 for="">{{ $t("inscription.fraisInscri") + " (dt)" }}</h6>
                <p>{{ classroom.inscription }}</p>
              </div>
              <div
                @click="changeModeP('M')"
                :class="[
                  'col-lg-4',
                  'mr-2',
                  'pt-2',
                  modePaiement === 'M'
                    ? 'custom-border-active'
                    : 'custom-border',
                ]"
              >
                <h6 for="">{{ $t("inscription.fraisMensuel") + " (dt)" }}</h6>
                <p>{{ classroom.monthly }}</p>
              </div>
              <div
                @click="changeModeP('T')"
                :class="[
                  'col-lg-4',
                  'pt-2',
                  modePaiement === 'T'
                    ? 'custom-border-active'
                    : 'custom-border',
                ]"
                v-if="!!classroom.tranches && classroom.tranches.length > 0"
              >
                <h6 for="">{{ $t("inscription.installmentFees") }} (dt)</h6>
                <li v-for="t in classroom.tranches" :key="t.trancheNumber">
                  {{
                    $t("inscription.installment") +
                    " " +
                    t.trancheNumber +
                    " : " +
                    t.amount
                  }}
                </li>
              </div>
              <a-alert
                class="mt-2"
                :message="
                  $t('inscription.selectedPaymentMode') +
                  (modePaiement === 'T'
                    ? $t('inscription.byInstallments')
                    : $t('inscription.monthly'))
                "
                type="info"
                show-icon
              />
            </div>
            <div class="row">
              <a-divider />

              <div class="col-lg-12 mb-2">
                <div class="row fee-month-header">
                  <div :class="modePaiement === 'M' ? 'col-5' : 'col-6'">
                    <div class="fee-month-header-label">
                      {{ $t("inscription.fees") }}
                    </div>
                  </div>
                  <div :class="modePaiement === 'M' ? 'col-3' : 'col-6'">
                    <div class="fee-month-header-label">
                      {{ $t("inscription.included") }}
                    </div>
                  </div>
                  <div v-if="modePaiement === 'M'" class="col-4">
                    <div class="fee-month-header-label">
                      {{ $t("inscription.discount") }}
                    </div>
                  </div>
                </div>
                <div v-for="(value, key) in includedFees" :key="key">
                  <div class="row align-items-center fee-month-row">
                    <div :class="modePaiement === 'M' ? 'col-5' : 'col-6'">
                      <a-form-item class="m-0">
                        <a-tag style="width: 100%; font-size: 15px" class="mt-1"
                          >{{ formatFeeName(key) }}
                        </a-tag>
                      </a-form-item>
                    </div>
                    <div :class="modePaiement === 'M' ? 'col-3' : 'col-6'">
                      <a-form-item class="m-0">
                        <a-switch
                          :checked="includedFees[key]"
                          :key="key"
                          @change="
                            (e) => {
                              changeIncludedFees(key, e);
                            }
                          "
                        >
                          <a-icon slot="checkedChildren" type="check" />
                          <a-icon slot="unCheckedChildren" type="close" />
                        </a-switch>
                      </a-form-item>
                    </div>
                    <div v-if="modePaiement === 'M'" class="col-4">
                      <a-form-item class="m-0">
                        <div
                          v-if="getMonthDiscount(key)"
                          class="month-discount-inline"
                        >
                          <a-switch
                            size="small"
                            :checked="getMonthDiscount(key).enabled"
                            @change="
                              (e) => toggleMonthDiscountEnabled(key, e)
                            "
                            class="mr-2"
                          >
                          </a-switch>
                          <a-input-number
                            :disabled="!getMonthDiscount(key).enabled"
                            :min="1"
                            :max="
                              reductionType === 'pourcentage'
                                ? 100
                                : classroom.monthly
                            "
                            :value="getMonthDiscount(key).value"
                            :formatter="discountFormatter"
                            :parser="discountParser"
                            @change="
                              (val) => onMonthDiscountValueChange(key, val)
                            "
                            style="width: 120px"
                          />
                        </div>
                      </a-form-item>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="form-actions mt-0">
              <a-button
                style="width: 150px"
                type="primary"
                htmlType="submit"
                :loading="loadingInscription"
                class="mr-3"
              >
                {{ $t("action.ajouter") }}
              </a-button>
              <a-button
                key="back"
                @click="handleCancel"
                :disabled="loadingInscription"
              >
                {{ $t("action.annuler") }}
              </a-button>
            </div>
          </a-form>
        </a-modal>
      </div>

      <!-- Parent sheet column selection modal -->
      <a-modal
        v-model="parentSheetModalVisible"
        :title="$t('ficheClasse.parentSheetSelectColumnsTitle')"
        :width="520"
        @cancel="closeParentSheetModal"
      >
        <p class="text-muted mb-3">
          {{ $t("ficheClasse.parentSheetSelectColumnsHint") }}
        </p>
        <div class="mb-3">
          <a-button size="small" class="mr-2" @click="selectAllParentSheetColumns">
            {{ $t("payment.selectAll") }}
          </a-button>
          <a-button size="small" @click="deselectAllParentSheetColumns">
            {{ $t("payment.deselectAll") }}
          </a-button>
        </div>
        <a-checkbox-group
          v-model="selectedParentSheetColumns"
          class="parent-sheet-column-group"
        >
          <a-row>
            <a-col
              v-for="col in parentSheetColumnOptions"
              :key="col.key"
              :span="24"
              class="mb-2"
            >
              <a-checkbox :value="col.key">
                {{ $t(col.labelKey) }}
              </a-checkbox>
            </a-col>
          </a-row>
        </a-checkbox-group>
        <template slot="footer">
          <a-button @click="closeParentSheetModal">
            {{ $t("action.annuler") }}
          </a-button>
          <a-button type="primary" @click="confirmParentSheetExport">
            <a-icon type="printer" />
            {{ $t("action.imprimer") }}
          </a-button>
        </template>
      </a-modal>

      <!-- Edit Scolarité Fees Modal -->
      <edit-scolarite-fees-modal
        :visible="editModalVisible"
        :scolarite-data="selectedScolarite"
        @close="closeEditModal"
      />
    </div>
  </div>
</template>
<script>
/* eslint-disable */
import JsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import router from "@/router";
import apiClient from "@/services/axios";
import { showApiError } from "@/utilities/showApiError";
import moment from "moment";
import { mapState } from "vuex";
import { PageHeader } from "ant-design-vue";
var init = true;
import { formatFeeName as formatFeeNameUtil, dateRange } from "@/utilities/settings";
import { getFullSchoolName } from "@/utilities/buildingTypeLabel";
import { getLevels } from "@/config/LevelConstants";
import EditScolariteFeesModal from "@/components/comptabilite/EditScolariteFeesModal.vue";
import {
  isKenyanSchool,
  getReductionTypeLabel,
} from "@/utilities/countrySpecific";

const DEFAULT_MONTH_DISCOUNT_VALUE = 50;

export default {
  computed: {
    ...mapState(["settings", "user"]),
    countryIsTN() {
      const building = this.user.building.filter(
        (el) => el.dbName == this.settings.activeBuilding
      )[0] || {};
      return building.countryCode == "TN";
    },
    activeBuilding() {
      return (
        (this.user.building || []).find(
          (el) => el.dbName == this.settings.activeBuilding
        ) || {}
      );
    },
    levelLabels() {
      return getLevels(
        this.$t.bind(this),
        this.activeBuilding.type || [],
        this.activeBuilding.countryCode
      );
    },
    classRoomsGroupedByLevel() {
      const list = this.classRoomList || [];
      const groupsMap = {};

      list.forEach((classroom) => {
        const rawLevel = classroom.level;
        const hasLevel =
          rawLevel !== undefined &&
          rawLevel !== null &&
          rawLevel !== "" &&
          !Number.isNaN(Number(rawLevel));
        const key = hasLevel ? String(rawLevel) : "other";
        if (!groupsMap[key]) {
          groupsMap[key] = {
            key,
            label: hasLevel
              ? this.levelLabels[key] ||
                `${this.$t("etablissement.niveau") || "Niveau"} ${key}`
              : this.$t("scolarite.other"),
            sortLevel: hasLevel ? Number(rawLevel) : Number.POSITIVE_INFINITY,
            classrooms: [],
          };
        }
        groupsMap[key].classrooms.push(classroom);
      });

      return Object.values(groupsMap)
        .map((group) => ({
          ...group,
          classrooms: group.classrooms.slice().sort((a, b) => {
            const aAff = a.classroomType === "affectation" ? 0 : 1;
            const bAff = b.classroomType === "affectation" ? 0 : 1;
            if (aAff !== bAff) return aAff - bAff;
            return String(a.name || "").localeCompare(String(b.name || ""));
          }),
        }))
        .sort((a, b) => a.sortLevel - b.sortLevel);
    },
    columns() {
      return [
        {
          title: this.$t("inscription.nom"),
          dataIndex: "fullName",
          key: "fullName",
          scopedSlots: {
            customRender: "fullName",
            filterDropdown: "filterDropdown",
            filterIcon: "filterIcon",
          },
          onFilter: (value, record) =>
            record.fullName
              .toString()
              .toLowerCase()
              .includes(value.toLowerCase()),
          onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
              setTimeout(() => {
                this.searchInput.focus();
              }, 0);
            }
          },
        },
        {
          title: this.$t("inscription.dateNaissance"),
          dataIndex: "BirthDate",
          key: "BirthDate",
          scopedSlots: {
            customRender: "BirthDate",
            filterDropdown: "filterDropdown",
            filterIcon: "filterIcon",
          },
          onFilter: (value, record) =>
            record.BirthDate.toString()
              .toLowerCase()
              .includes(value.toLowerCase()),
          onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
              setTimeout(() => {
                this.searchInput.focus();
              }, 0);
            }
          },
        },
        {
          title: this.$t("inscription.sexe"),
          dataIndex: "gender",
          key: "gender",
          scopedSlots: {
            customRender: "gender",
          },
          filters: [
            { text: this.$t("profil.garcon"), value: "male" },
            { text: this.$t("profil.fille"), value: "female" },
          ],
          onFilter: (value, record) => record.gender.indexOf(value) === 0,
        },
        {
          title: this.$t("inscription.tels"),
          dataIndex: "phone",
          key: "phone",
          scopedSlots: {
            customRender: "phone",
            filterDropdown: "filterDropdown",
            filterIcon: "filterIcon",
          },
          onFilter: (value, record) => {
            const phone = record.phone;
            if (typeof phone === "object" && phone !== null) {
              const fatherPhone = String(phone.father || "");
              const motherPhone = String(phone.mother || "");
              return (
                fatherPhone.toLowerCase().includes(value.toLowerCase()) ||
                motherPhone.toLowerCase().includes(value.toLowerCase())
              );
            }
            return phone.toString().toLowerCase().includes(value.toLowerCase());
          },
          onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
              setTimeout(() => {
                this.searchInput.focus();
              }, 0);
            }
          },
        },
        {
          title: this.$t("inscription.parent"),
          dataIndex: "parent",
          key: "parent",
          scopedSlots: {
            customRender: "parent",
            filterDropdown: "filterDropdown",
            filterIcon: "filterIcon",
          },
          onFilter: (value, record) => {
            const parent = record.parent;
            if (typeof parent === "object" && parent !== null) {
              const fatherName = parent.father || "";
              const motherName = parent.mother || "";
              return (
                fatherName.toLowerCase().includes(value.toLowerCase()) ||
                motherName.toLowerCase().includes(value.toLowerCase())
              );
            }
            return parent
              .toString()
              .toLowerCase()
              .includes(value.toLowerCase());
          },
          onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
              setTimeout(() => {
                this.searchInput.focus();
              }, 0);
            }
          },
        },
        {
          title: this.$t("inscription.changeClassroom"),
          dataIndex: "changeClassroom",
          key: "changeClassroom",
          scopedSlots: {
            customRender: "changeClassroom",
          },
        },
        {
          title: this.$t("inscription.changeGroup"),
          dataIndex: "changeGroup",
          key: "changeGroup",
          scopedSlots: {
            customRender: "changeGroup",
          },
        },
        {
          title: this.$t("action.actions"),
          dataIndex: "actions",
          key: "actions",
          scopedSlots: {
            customRender: "actions",
          },
        },
      ];
    },
    includedFeesKeys() {
      return Object.keys(this.includedFees);
    },
    // Months currently toggled ON — discount follows these, not the list order alone
    activeIncludedMonthKeys() {
      return this.includedFeesKeys.filter((key) => this.includedFees[key]);
    },
    firstMonthKey() {
      return this.activeIncludedMonthKeys.length
        ? this.activeIncludedMonthKeys[0]
        : null;
    },
    lastMonthKey() {
      const keys = this.activeIncludedMonthKeys;
      return keys.length ? keys[keys.length - 1] : null;
    },
    parentSheetColumnOptions() {
      const countryIsKE = isKenyanSchool();
      const uniqueIdKey = countryIsKE
        ? "profil.nemisNumber"
        : "ficheClasse.identifiantUnique";
      return [
        { key: "fullName", labelKey: "ficheClasse.nomPrenom" },
        ...(!countryIsKE
          ? [
              {
                key: "fullNameFr",
                labelKey: "ficheClasse.nomPrenomFr",
              },
            ]
          : []),
        { key: "uniqueId", labelKey: uniqueIdKey },
        { key: "birthDate", labelKey: "ficheClasse.dateNaissance" },
        { key: "parentName", labelKey: "ficheClasse.nomParent" },
        { key: "phone", labelKey: "ficheClasse.telephone" },
        { key: "email", labelKey: "liste.email" },
        { key: "address", labelKey: "liste.adresse" },
      ];
    },
  },
  components: {
    "a-page-header": PageHeader,
    EditScolariteFeesModal,
  },
  props: {
    classId: {
      required: true,
    },
  },
  async created() {
    this.tableLoading = true;
    const building = this.user.building.find(
      (el) => el.dbName == this.settings.activeBuilding
    );
    if (building) this.reductionType = building.reductionType || "pourcentage";

    apiClient
      .post("/classrooms/filter", {
        query: { includeAffectation: true },
        aggregation: {
          $project: {
            name: 1,
            level: 1,
            inscription: 1,
            monthly: 1,
            tranches: 1,
            status: 1,
            classroomType: 1,
            enrollmentConfirmed: 1,
          },
        },
      })
      .then((res) => {
        this.classroom = res.data.find((c) => c._id == this.classId);
        if (this.classroom && this.classroom.enrollmentConfirmed == null) {
          this.$set(this.classroom, "enrollmentConfirmed", true);
        }
        this.classRoomList = res.data.filter((c) => c.status == "active");
        if (this.classroom.status == "inactive")
          this.classRoomList.push(this.classroom);
      })
      .catch((e) => {
        console.error(e);
        this.$message.error(this.$t("error.classNotFound"));
        router.go(-1);
      });

    await apiClient
      .post("/students/filter", {
        query: {
          status: "active",
        },
        aggregation: {
          $project: {
            firstName: 1,
            lastName: 1,
            middleName: 1,
            classRoom: 1,
            schoolarYearsHistory: 1,
          },
        },
      })
      .then((res) => {
        res.data.forEach((elem) => {
          if (
            !elem.classRoom ||
            !elem.schoolarYearsHistory[this.settings.activeSchoolarYear]
          )
            this.eleves.push(elem);
        });
      })
      .catch((e) => {
        console.error(e);
        this.$message.error(this.$t("error.studentListNotFound"));
      });

    await this.getStudentTable();
    await this.fetchGroupAssignments();
    this.setIncludedFees();
  },
  data() {
    this.dateFormat = moment("DD MM-YYYY");
    return {
      tableLoading: false,
      hasDefaultReduction: false,
      defaultReduction: 0,
      firstMonthDiscount: {
        enabled: true,
        value: DEFAULT_MONTH_DISCOUNT_VALUE,
      },
      lastMonthDiscount: {
        enabled: true,
        value: DEFAULT_MONTH_DISCOUNT_VALUE,
      },
      classroomCount: 0,
      classroom: {},
      reductionType: "precentage",
      data: [],
      firstPage: true,
      selectedClass: null,
      changeMois: null,
      defaultValue: null,
      editingKey: "",
      editable: false,
      searchText: "",
      searchInput: null,
      searchedColumn: "",
      loadingInscription: false,
      visibleModal: false,
      filtredTable: [],
      form: this.$form.createForm(this),
      date: null,
      fraisInscription: null,
      fraisMensuel: null,
      eleves: [],
      modePaiement: "M",
      currentFeesRange: {},
      selectedLanguageParent: "fr",
      selectedLanguageClass: "fr",
      includedFees: {},
      changeClassRoomDisable: false,
      changeGroupDisable: false,
      classRoomList: [],
      Assignments: {},
      editModalVisible: false,
      selectedScolarite: null,
      loadingSave: false,
      parentSheetModalVisible: false,
      parentSheetExportType: null,
      parentSheetExportLang: null,
      selectedParentSheetColumns: [],
      enrollmentConfirmationLoading: false,
    };
  },
  methods: {
    isKenyanSchool,
    confirmEnrollmentValidation() {
      const content = this.classroom.enrollmentConfirmed
        ? this.$t("inscription.cancelEnrollmentConfirmationTooltip", {
            name: this.classroom.name,
          })
        : this.$t("inscription.confirmEnrollmentTooltip", {
            name: this.classroom.name,
          });
      this.$confirm({
        title: this.$t("all.sureTo"),
        content: (h) => <div style="color:red"> {content} </div>,
        onOk: () => this.updateEnrollmentConfirmation(),
        onCancel: () => {},
      });
    },
    updateEnrollmentConfirmation() {
      const newState = !this.classroom.enrollmentConfirmed;
      this.enrollmentConfirmationLoading = true;

      return apiClient
        .patch("/classrooms/" + this.classroom._id, {
          data: { enrollmentConfirmed: newState },
        })
        .then(() => {
          this.$set(this.classroom, "enrollmentConfirmed", newState);
          this.$message.success(
            newState
              ? this.$t("inscription.enrollmentConfirmedMsg", {
                  name: this.classroom.name,
                })
              : this.$t("inscription.enrollmentUnconfirmedMsg", {
                  name: this.classroom.name,
                })
          );
        })
        .catch((error) => {
          console.error(error);
          this.$message.error(this.$t("error.impoModifi"));
          throw error;
        })
        .finally(() => {
          this.enrollmentConfirmationLoading = false;
        });
    },
    formatFeeName(key, daily = false) {
      return formatFeeNameUtil(key, daily, this.$t.bind(this));
    },

    // Function to format parent names for Arabic PDF based on language
    formatParentNamesForArabic(fatherName, motherName) {
      // Check if names contain Arabic characters
      const hasArabicText = /[\u0600-\u06FF]/.test(fatherName + motherName);

      if (hasArabicText) {
        return `الأب : ${fatherName}\nالأم : ${motherName}`;
      } else {
        return `${fatherName} : الأب\n${motherName} : الأم`;
      }
    },


    localizedGroupLabel(name) {
      if (!name) return "";
      const match = String(name).match(/(\d+)/);
      if (match && match[1]) {
        return `${this.$t("sms.groupe")} ${match[1]}`;
      }
      return `${this.$t("sms.groupe")}`;
    },
    localizedGroupLabelArabic(name) {
      if (!name || name === "") return "";
      const nameStr = String(name).trim();
      if (nameStr === "") return "";

      const match = nameStr.match(/(\d+)/);
      if (match && match[1]) {
        return `مجموعة ${match[1]}`; // Direct Arabic for "Group"
      }
      return "مجموعة"; // Direct Arabic for "Group"
    },
    changeIncludedFees(key, e) {
      // Vue 2: must $set so first/last discount targets recompute
      this.$set(this.includedFees, key, e);
    },
    // Returns the discount config for this month row, or null if none
    getMonthDiscount(key) {
      if (this.modePaiement !== "M" || !key) return null;
      if (key === this.firstMonthKey) return this.firstMonthDiscount;
      if (key === this.lastMonthKey && key !== this.firstMonthKey)
        return this.lastMonthDiscount;
      return null;
    },
    toggleMonthDiscountEnabled(key, enabled) {
      const discount = this.getMonthDiscount(key);
      if (!discount) return;
      discount.enabled = enabled;
      if (enabled && (!discount.value || Number(discount.value) <= 0)) {
        discount.value = DEFAULT_MONTH_DISCOUNT_VALUE;
      }
    },
    onMonthDiscountValueChange(key, val) {
      const discount = this.getMonthDiscount(key);
      if (!discount) return;
      // When discount is active, value must stay > 0
      if (discount.enabled && (val === null || val === undefined || val <= 0)) {
        discount.value = DEFAULT_MONTH_DISCOUNT_VALUE;
        return;
      }
      discount.value = val;
    },
    getReductionTypeLabel() {
      return getReductionTypeLabel(this.reductionType);
    },
    discountFormatter(value) {
      if (value === undefined || value === null || value === "") return "";
      if (this.reductionType === "pourcentage") {
        return `${value}%`;
      }
      return `${this.getReductionTypeLabel()} ${value}`.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        ","
      );
    },
    discountParser(value) {
      if (value === undefined || value === null) return "";
      if (this.reductionType === "pourcentage") {
        return String(value).replace("%", "");
      }
      const unit = this.getReductionTypeLabel();
      const unitRegex = new RegExp(
        unit.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "\\s?",
        "g"
      );
      return String(value).replace(unitRegex, "").replace(/,/g, "");
    },
    computeReductionPercentage(discountValue) {
      const value = Number(discountValue) || 0;
      if (this.reductionType === "amount") {
        const base = Number(this.classroom.monthly) || 0;
        if (!base) return 0;
        return Math.min(100, (value / base) * 100);
      }
      return Math.min(100, value);
    },
    resetMonthDiscounts() {
      this.firstMonthDiscount = {
        enabled: true,
        value: DEFAULT_MONTH_DISCOUNT_VALUE,
      };
      this.lastMonthDiscount = {
        enabled: true,
        value: DEFAULT_MONTH_DISCOUNT_VALUE,
      };
    },
    changeModeP(v) {
      this.modePaiement = v;
      this.setIncludedFees();
    },
    setIncludedFees(value = null) {
      const activeSchoolarYear = this.settings.activeSchoolarYear;
      const schoolarYears = this.settings.schoolarYears;
      let start = value;
      let end = null;
      schoolarYears.forEach((elem) => {
        if (elem._id == activeSchoolarYear) {
          end = elem.end;
          if (!start) start = elem.start;
        }
      });

      const range = this.dateRange(
        moment(start).format("YYYY-MM-DD"),
        moment(end).format("YYYY-MM-DD")
      );

      const fees = {};
      if (this.classroom.monthly && this.modePaiement == "M") {
        range.forEach((month) => {
          fees[month] = true;
        });
      }
      if (this.classroom.tranches && this.modePaiement == "T") {
        this.classroom.tranches.forEach((tranche) => {
          fees[tranche.trancheNumber] = true;
        });
      }
      // Replace the whole object so every month key is reactive
      this.includedFees = fees;

      this.resetMonthDiscounts();
    },

    dateRange,
    async getStudentTable() {
      let match = {};
      match[`schoolarYearsHistory.${this.settings.activeSchoolarYear}`] =
        this.classId;
      await apiClient
        .post("/students/filter", {
          query: { status: "active" },
          aggregation: [
            {
              $match: match,
            },
            {
              $lookup: {
                from: "parents",
                localField: "parent",
                foreignField: "_id",
                as: "parent",
              },
            },
            {
              $project: {
                _id: 1,
                firstName: 1,
                firstNameFr: 1,
                lastName: 1,
                lastNameFr: 1,
                middleName: 1,
                middleNameFr: 1,
                parent: {
                  father: 1,
                  mother: 1,
                },
                gender: 1,
                BirthDate: 1,
                name: 1,
                uniqueId: 1,
                nemisNumber: 1,
                adress: 1,
                schoolarYearsHistory: 1,
              },
            },
          ],
        })
        .then((res) => {
          this.classroomCount = res.data.length;
          this.data = res.data.map((elem) => {
            // Check if parent array exists and has at least one element
            let parentInfo = {
              father: "",
              mother: "",
            };
            let phoneInfo = {
              father: "",
              mother: "",
            };
            let emailInfo = {
              father: "",
              mother: "",
            };
            let familyAddress = elem.adress || "";

            if (elem.parent && elem.parent.length > 0 && elem.parent[0]) {
              const parentData = elem.parent[0];
              const fatherName = parentData.father
                ? `${parentData.father.firstName || ""} ${
                    parentData.father.lastName || ""
                  }`.trim()
                : "";

              // Safely access mother information
              const motherName = parentData.mother
                ? `${parentData.mother.firstName || ""} ${
                    parentData.mother.lastName || ""
                  }`.trim()
                : "";

              // Set parent names as separate objects
              parentInfo = {
                father: fatherName,
                mother: motherName,
              };

              // Safely access phone information
              const fatherPhone = parentData.father?.phone || "";
              const motherPhone = parentData.mother?.phone || "";

              phoneInfo = {
                father: fatherPhone,
                mother: motherPhone,
              };

              emailInfo = {
                father: parentData.father?.email || "",
                mother: parentData.mother?.email || "",
              };

              if (!familyAddress) {
                familyAddress =
                  parentData.father?.adress ||
                  parentData.mother?.adress ||
                  "";
              }
            }

            return {
              key: elem._id,
              _id: elem._id,
              fullName: elem.firstName + (elem.middleName ? " " + elem.middleName : "") + " " + elem.lastName,
              fullNameFr: [elem.firstNameFr, elem.middleNameFr, elem.lastNameFr]
                .filter(Boolean)
                .join(" "),
              parent: parentInfo,
              BirthDate: moment(elem.BirthDate).format("DD/MM/YYYY"),
              gender: elem.gender,
              phone: phoneInfo,
              email: emailInfo,
              adress: familyAddress,
              schoolarYearsHistory: elem.schoolarYearsHistory,
              uniqueId: elem.uniqueId ? elem.uniqueId : "",
              nemisNumber: elem.nemisNumber ? elem.nemisNumber : "",
            };
          });
          this.filtredTable = this.data.map((item) => ({ ...item }));

          this.cacheData = this.data.map((item) => ({ ...item }));
        });

      this.tableLoading = false;
      //this.filtredTable = this.activeData;
    },
    async fetchGroupAssignments() {
      try {
        const res = await apiClient.post("/scheduleGroup/byClassroom", {
          classRoom: this.classId,
        });
        const assignments =
          res.data && res.data.assignments ? res.data.assignments : {};
        this.groupAssignments = assignments;
        this.data = this.data.map((row) => ({
          ...row,
          groupName: assignments[row._id] || "",
        }));
        this.filtredTable = this.data.map((item) => ({ ...item }));
      } catch (e) {
        console.error(e);
      }
    },
    tableChanged(pagination, filters, sorter, extra) {
      this.filtredTable = extra.currentDataSource;
    },
    moment,
    showModal() {
      this.visibleModal = true;
    },
    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text
          .toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      );
    },
    addInscription(e) {
      this.loadingInscription = true;
      e.preventDefault();
      this.form.validateFieldsAndScroll((err, values) => {
        if (!err) {
          const activeSchoolarYear = this.settings.activeSchoolarYear;
          const schoolarYears = this.settings.schoolarYears;
          let data = { ...values };
          schoolarYears.forEach((elem) => {
            if (elem._id == activeSchoolarYear) {
              data.end = elem.end;
              if (!values.start) data.start = elem.start;
            }
          });
          delete data.fraisMensuel;
          delete data.fraisInscription;

          data.typeAS = "S";
          data.classroom = this.classId;
          data.defaultReduction = this.defaultReduction;
          data.mode = this.modePaiement;
          const filteredRepReduction = Object.keys(this.includedFees)
            .filter((key) => this.includedFees[key] === true)
            .reduce((obj, key) => {
              obj[key] = false;
              return obj;
            }, {});
          let repReduction = { ...filteredRepReduction };
          data.mode = this.modePaiement;
          data.repReduction = repReduction;
          if (this.modePaiement == "M") data.months = filteredRepReduction;
          else data.tranches = filteredRepReduction;
          //default rep reduction is 0
          data.repReduction = Object.keys(filteredRepReduction).reduce(
            (obj, key) => {
              obj[key] = 0;
              return obj;
            },
            {}
          );
          data.repReduction["inscription"] = 0;

          // Apply the first/last month discount on top of the base reduction
          if (this.modePaiement == "M") {
            const firstKey = this.firstMonthKey;
            const lastKey = this.lastMonthKey;
            if (
              this.firstMonthDiscount.enabled &&
              firstKey &&
              Object.prototype.hasOwnProperty.call(data.repReduction, firstKey)
            ) {
              data.repReduction[firstKey] = this.computeReductionPercentage(
                this.firstMonthDiscount.value
              );
            }
            if (
              this.lastMonthDiscount.enabled &&
              lastKey &&
              lastKey !== firstKey &&
              Object.prototype.hasOwnProperty.call(data.repReduction, lastKey)
            ) {
              data.repReduction[lastKey] = this.computeReductionPercentage(
                this.lastMonthDiscount.value
              );
            }
          }

          apiClient
            .put(`/payment/registerStudents`, data)
            .then(async (res) => {
              await this.getStudentTable();
              this.handleCancel();
              this.eleves = this.eleves.filter(
                (elem) => !values.students.includes(elem._id)
              );
              this.$message.success(this.$t("success.inscriAjout"));
            })
            .catch((e) => {
              this.$message.error(this.$t("error.inscriEchoue"));
              console.log(e);
            })
            .finally(() => {
              this.loadingInscription = false;
            });
        } else {
          this.loadingInscription = false;
        }
      });
    },

    handleCancel() {
      this.form.resetFields();
      this.defaultReduction = 0;
      this.hasDefaultReduction = false;
      this.resetMonthDiscounts();
      this.visibleModal = false;
    },
    handleSearch(selectedKeys, confirm, dataIndex) {
      confirm();
      this.searchText = selectedKeys[0];
      this.searchedColumn = dataIndex;
    },

    handleReset(clearFilters) {
      clearFilters();
      this.searchText = "";
    },
    handleChange(value, key, column) {
      const newData = [...this.data];
      const target = newData.filter((item) => key === item.key)[0];
      if (target) {
        target[column] = value;
        this.data = newData;
      }
    },
    async changeClassRoom(calssRoomId, record) {
      this.changeClassRoomDisable = true;

      const selectedClassroomId = calssRoomId;
      const student = record;
      const oldClassRoom = this.classroom;

      await apiClient
        .patch("/payment/changeclass/" + student._id, {
          oldClassroomId: oldClassRoom._id,
          newClassroomId: selectedClassroomId,
        })
        .then((res) => {
          const newClassRoom = this.classRoomList.find(
            (c) => c._id == selectedClassroomId
          );
          this.$message.success(
            this.$t("success.eleveDemeangeAuClasse", {
              name: newClassRoom.name,
            })
          );
          this.getStudentTable();
          this.setIncludedFees();
        })
        .catch((error) => {
          showApiError(this, error);
        })
        .finally(() => {
          this.changeClassRoomDisable = false;

          this.loadingClassroomChange = false;
        });
      //////
    },
    changeGroup(groupValue, record) {
      this.changeGroupDisable = true;
      const classRoom = this.classId;
      const student = record._id;

      const request =
        groupValue === "" || groupValue === null
          ? apiClient.patch("/scheduleGroup/unassign", { classRoom, student })
          : apiClient.put("/scheduleGroup/assign", {
              classRoom,
              name: groupValue,
              student,
            });

      request
        .then(() => {
          this.$message.success(
            groupValue
              ? this.$t("success.groupUpdated")
              : this.$t("success.studentRemovedFromGroup")
          );
          const newGroup = groupValue || "";
          record.groupName = newGroup;
          this.groupAssignments[student] = newGroup;
          const dataIndex = this.data.findIndex((r) => r._id === student);
          if (dataIndex !== -1) {
            const updated = { ...this.data[dataIndex], groupName: newGroup };
            this.$set(this.data, dataIndex, updated);
          }
          const filtIndex = this.filtredTable.findIndex(
            (r) => r._id === student
          );
          if (filtIndex !== -1) {
            const updatedF = {
              ...this.filtredTable[filtIndex],
              groupName: newGroup,
            };
            this.$set(this.filtredTable, filtIndex, updatedF);
          }
        })
        .catch((err) => {
          console.error(err);
          this.$message.error(this.$t("error.groupUpdateFailed"));
        })
        .finally(() => {
          this.changeGroupDisable = false;
        });
    },
    formatDate(date, locale = "ar") {
      return new Intl.DateTimeFormat(locale, {
        year: "numeric",
        month: "long",
        day: "numeric",
      }).format(date);
    },
    disabledDate(current) {
      const activeSchoolarYear = this.settings.activeSchoolarYear;
      const schoolarYears = this.settings.schoolarYears;
      const schoolarYear = schoolarYears.find(
        (elem) => elem._id == activeSchoolarYear
      );
      const { start, end } = schoolarYear;
      return (current && current.isAfter(end)) || current.isBefore(start);
    },

    async generateFicheClassePDFar() {
      // Refresh group assignments before generating PDF
      await this.fetchGroupAssignments();

      const doc = new JsPDF({ orientation: "portrait" });
      doc.setFont("Amiri", "normal");

      let imgData = null;
      let imgLoaded = false;

      if (this.settings.image && Array.isArray(this.settings.image)) {
        for (const img of this.settings.image) {
          if (this.settings.activeBuilding === img.db && img.logo) {
            try {
              imgData = new Image();
              imgData.src = img.logo;
              imgLoaded = true;
              break;
            } catch (error) {
              console.error("Error loading image:", error);
            }
          }
        }
      }

      // Calculate logo and text positions with consistent margins for Arabic (RTL)
      const pageWidth = doc.internal.pageSize.getWidth();
      const leftMargin = 10;
      const logoWidth = 30;
      const logoHeight = 30;
      const logoX = leftMargin; // Logo on left for Arabic (RTL)
      const textX = pageWidth - leftMargin; // Text on right for Arabic (RTL) with same margin

      if (imgLoaded && imgData) {
        try {
          doc.addImage(imgData, "JPEG", logoX, 10, logoWidth, logoHeight);
        } catch (error) {
          // Continue with PDF generation even if image fails
        }
      }

      const schoolDetails = this.user.building.filter(
        (el) => el.dbName == this.settings.activeBuilding
      )[0];
      const delegation = schoolDetails?.delegeRegional || "غير متوفر";

      const today = this.formatDate(new Date(), "ar-tn");
      doc.setFontSize(12);

      const { start, end } = this.settings.schoolarYears.find(
        (el) => el._id == this.settings.activeSchoolarYear
      );

      const startYear = new Date(start).getFullYear();
      const endYear = new Date(end).getFullYear();
      const schoolarYearName = `${endYear} / ${startYear}`;

      doc.text(`السنة الدراسية: ${schoolarYearName}`, textX, 15, {
        align: "right",
      });
      doc.text(`المندوبية الجهوية ب: ${delegation}`, textX, 25, {
        align: "right",
      });
      doc.text(`التاريخ: ${today}`, textX, 35, { align: "right" });

      doc.text(
        getFullSchoolName(schoolDetails, null, "ar-AR"),
        105,
        50,
        {
          align: "center",
        }
      );
      doc.text("قائمة أسماء التلاميذ", 105, 60, { align: "center" });

      const className = this.classroom?.name || "غير متوفر";
      // Check if className contains Arabic text
      const hasArabicText = /[\u0600-\u06FF]/.test(className);
      const sectionText = hasArabicText
        ? `القسم : ${className}`
        : `${className} : القسم`;

      const textWidth = doc.getTextWidth(sectionText);
      const pageCenterX = doc.internal.pageSize.width / 2;

      doc.text(sectionText, pageCenterX, 70, { align: "center" });

      const rows = this.filtredTable.map((student, index) => [
        student.gender === "male" ? "ذكر" : "أنثى",
        this.localizedGroupLabelArabic(student.groupName),
        student.BirthDate,
        student.fullName,
        isKenyanSchool() ? (student.nemisNumber || "") : (student.uniqueId || ""),
        index + 1,
      ]);

      const boys = this.filtredTable.filter(
        (student) => student.gender === "male"
      ).length;
      const girls = this.filtredTable.filter(
        (student) => student.gender === "female"
      ).length;
      const total = this.filtredTable.length;

      rows.push([total, "", "", "", "", "المجموع"]);
      rows.push([boys, "", "", "", "", "مجموع الذكور"]);
      rows.push([girls, "", "", "", "", "مجموع الإناث"]);

      doc.autoTable({
        theme: "grid",
        styles: {
          font: "Amiri",
          halign: "right",
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.5,
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          lineWidth: 0.5,
          lineColor: [0, 0, 0],
          fontStyle: "bold",
        },
        bodyStyles: {
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.5,
        },
        head: [
          [
            "الجنس",
            "المجموعة",
            "تاريخ الولادة",
            "الاسم واللقب",
            "المعرف الوحيد",
            "العدد الرتبي",
          ],
        ],
        body: rows,
        startY: 80,
        didParseCell: (data) => {
          const rowIndex = data.row.index;
          const columnIndex = data.column.index;
          const totalStudents = this.filtredTable.length;

          if (rowIndex >= totalStudents) {
            if (columnIndex === 0) {
              data.cell.colSpan = 5;
              data.cell.styles.halign = "center";
              data.cell.styles.fontStyle = "bold";
            }

            if (columnIndex === 1) {
              data.cell.styles.lineWidth = 0.5;
              data.cell.text = [""];
            }
            if (columnIndex === 2 || columnIndex === 3 || columnIndex === 4) {
              data.cell.styles.lineWidth = 0;
              data.cell.text = [""];
            }
            if (columnIndex === 5) {
              data.cell.styles.halign = "right";
              data.cell.styles.fontStyle = "bold";
            }
          }
        },
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.text(`${i} `, 105, 290, { align: "center" });
      }

      // Save the document
      doc.save("بطاقة_الفصل.pdf");
    },

    async generateFicheClassePDF(lang = null) {
      // Determine language: use parameter if provided, otherwise use locale or country code
      if (!lang) {
        lang = this.countryIsTN ? "fr" : this.$i18n.locale.split("-")[0];
      }

      // Map lang to i18n locale
      const localeMap = {
        ar: "ar-AR",
        fr: "fr-FR",
        en: "en-US",
        sw: "sw-KE",
      };
      const targetLocale = localeMap[lang] || "fr-FR";

      // Helper to get translation without changing global locale
      const t = (key) => {
        try {
          const messages = this.$i18n.messages[targetLocale];
          const keys = key.split(".");
          let value = messages;
          for (const k of keys) {
            value = value?.[k];
          }
          return value || key;
        } catch (e) {
          return key;
        }
      };

      // Refresh group assignments before generating PDF
      await this.fetchGroupAssignments();

      const doc = new JsPDF({ orientation: "portrait" });
      doc.setFont("Amiri", "normal");

      let imgData = null;
      let imgLoaded = false;

      if (this.settings.image && Array.isArray(this.settings.image)) {
        for (const img of this.settings.image) {
          if (this.settings.activeBuilding === img.db && img.logo) {
            try {
              imgData = new Image();
              imgData.src = img.logo;
              imgLoaded = true;
              break;
            } catch (error) {
              console.error("Error loading image:", error);
            }
          }
        }
      }

      // Calculate logo position to match left text margin
      const pageWidth = doc.internal.pageSize.getWidth();
      const leftMargin = 10;
      const logoWidth = 30;
      const logoHeight = 30;
      const logoX = pageWidth - leftMargin - logoWidth; // Same margin on right as left

      if (imgLoaded && imgData) {
        try {
          doc.addImage(imgData, "JPEG", logoX, 10, logoWidth, logoHeight);
        } catch (error) {
          // Continue with PDF generation even if image fails
        }
      }

      const schoolDetails = this.user.building.filter(
        (el) => el.dbName == this.settings.activeBuilding
      )[0];
      const delegation = schoolDetails?.delegeRegional || t("ficheClasse.nonDisponible");

      const today = this.formatDate(new Date(), targetLocale);
      doc.setFontSize(12);

      const { start, end } = this.settings.schoolarYears.find(
        (el) => el._id == this.settings.activeSchoolarYear
      );

      const startYear = new Date(start).getFullYear();
      const endYear = new Date(end).getFullYear();
      const schoolarYearName = `${startYear} / ${endYear}`;

      const yearLabel = t("ficheClasse.anneeScolaireLabel");
      const delegationLabel = t("ficheClasse.delegeRegionalEducation");
      const dateLabel = t("ficheClasse.date");
      const listLabel = t("ficheClasse.listeEleves");
      const classLabel = t("ficheClasse.classe");

      doc.text(`${yearLabel} : ${schoolarYearName}`, leftMargin, 15, {
        align: "left",
      });
      doc.text(`${delegationLabel} : ${delegation}`, leftMargin, 25, {
        align: "left",
      });
      doc.text(`${dateLabel} : ${today}`, leftMargin, 35, { align: "left" });

      doc.text(
        getFullSchoolName(schoolDetails, null, targetLocale),
        105,
        50,
        {
          align: "center",
        }
      );
      doc.text(listLabel, 105, 60, { align: "center" });

      const className = this.classroom?.name || t("ficheClasse.nonDisponible");
      const sectionText = `${classLabel} : ${className}`;
      doc.text(sectionText, 105, 70, { align: "center" });

      const rows = this.filtredTable.map((student, index) => [
        index + 1,
        isKenyanSchool() ? (student.nemisNumber || "") : (student.uniqueId || ""),
        student.fullName,
        student.BirthDate,
        this.localizedGroupLabel(student.groupName),
        student.gender === "male"
          ? t("ficheClasse.garcon")
          : t("ficheClasse.fille"),
      ]);

      const boys = this.filtredTable.filter(
        (student) => student.gender === "male"
      ).length;
      const girls = this.filtredTable.filter(
        (student) => student.gender === "female"
      ).length;
      const total = this.filtredTable.length;

      rows.push([t("ficheClasse.total"), total, "", "", "", ""]);
      rows.push([t("ficheClasse.garcons"), boys, "", "", "", ""]);
      rows.push([t("ficheClasse.filles"), girls, "", "", "", ""]);

      doc.autoTable({
        theme: "grid",
        styles: {
          font: "Amiri",
          halign: "left",
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.5,
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          lineWidth: 0.5,
          lineColor: [0, 0, 0],
          fontStyle: "bold",
        },
        bodyStyles: {
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.5,
        },
        head: [
          [
            t("ficheClasse.numero"),
            isKenyanSchool() ? t("profil.nemisNumber") : t("ficheClasse.identifiantUnique"),
            t("ficheClasse.nomComplet"),
            t("ficheClasse.dateNaissance"),
            t("ficheClasse.group"),
            t("ficheClasse.sexe"),
          ],
        ],
        body: rows,
        startY: 80,
        didParseCell: (data) => {
          const rowIndex = data.row.index;
          const columnIndex = data.column.index;

          if (rowIndex >= this.filtredTable.length) {
            if (columnIndex === 1) {
              data.cell.colSpan = 5;

              data.cell.styles.halign = "center";

            }
            if (
              columnIndex === 2 ||
              columnIndex === 3 ||
              columnIndex === 4 ||
              columnIndex === 5
            ) {
              data.cell.styles.lineWidth = 0;
            }
          }
        },
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.text(`${i} `, 105, 290, { align: "center" });
      }

      const pdfFileName = t("ficheClasse.listeElevesPDF");
      doc.save(`${pdfFileName}.pdf`);
    },

    getDefaultParentSheetColumns() {
      return ["fullName", "uniqueId", "birthDate", "parentName", "phone"];
    },

    openParentSheetModal(exportType, lang = null) {
      this.parentSheetExportType = exportType;
      this.parentSheetExportLang = lang;
      this.selectedParentSheetColumns = this.getDefaultParentSheetColumns();
      this.parentSheetModalVisible = true;
    },

    closeParentSheetModal() {
      this.parentSheetModalVisible = false;
      this.parentSheetExportType = null;
      this.parentSheetExportLang = null;
    },

    selectAllParentSheetColumns() {
      this.selectedParentSheetColumns = this.parentSheetColumnOptions.map(
        (col) => col.key
      );
    },

    deselectAllParentSheetColumns() {
      this.selectedParentSheetColumns = [];
    },

    confirmParentSheetExport() {
      if (!this.selectedParentSheetColumns.length) {
        this.$message.warning(
          this.$t("ficheClasse.parentSheetSelectAtLeastOneColumn")
        );
        return;
      }

      const columnOrder = this.parentSheetColumnOptions.map((col) => col.key);
      const selectedColumns = [...this.selectedParentSheetColumns].sort(
        (a, b) => columnOrder.indexOf(a) - columnOrder.indexOf(b)
      );
      const exportType = this.parentSheetExportType;
      const exportLang = this.parentSheetExportLang;
      this.closeParentSheetModal();

      if (exportType === "pdf") {
        this.generateFicheParentPDF(exportLang, selectedColumns);
      } else if (exportType === "pdfAr") {
        this.generateFicheParentPDFar(selectedColumns);
      } else if (exportType === "excel") {
        this.generateFicheParentExcel(exportLang, selectedColumns);
      }
    },

    getParentSheetTranslationHelper(lang) {
      const localeMap = {
        ar: "ar-AR",
        fr: "fr-FR",
        en: "en-US",
        sw: "sw-KE",
      };
      const targetLocale = localeMap[lang] || "fr-FR";

      return (key) => {
        try {
          const messages = this.$i18n.messages[targetLocale];
          const keys = key.split(".");
          let value = messages;
          for (const k of keys) {
            value = value?.[k];
          }
          return value || key;
        } catch (e) {
          return key;
        }
      };
    },

    getParentSheetColumnHeader(columnKey, t, isArabic = false) {
      if (isArabic) {
        const arabicHeaders = {
          fullName: "الاسم واللقب",
          fullNameFr: "الاسم واللقب بالفرنسية",
          uniqueId: isKenyanSchool() ? "NEMIS" : "المعرف الوحيد",
          birthDate: "تاريخ الميلاد",
          parentName: "إسم ولي الأمر",
          phone: "الهاتف",
          email: "البريد الإلكتروني",
          address: "العنوان",
        };
        return arabicHeaders[columnKey] || columnKey;
      }

      const headerKeys = {
        fullName: "ficheClasse.nomPrenom",
        fullNameFr: "ficheClasse.nomPrenomFr",
        uniqueId: isKenyanSchool()
          ? "profil.nemisNumber"
          : "ficheClasse.identifiantUnique",
        birthDate: "ficheClasse.dateNaissance",
        parentName: "ficheClasse.nomParent",
        phone: "ficheClasse.telephone",
        email: "liste.email",
        address: "liste.adresse",
      };

      return t(headerKeys[columnKey] || columnKey);
    },

    formatParentSheetDualValue(values, t, { isArabic = false, separator = "\n" } = {}) {
      const { father, mother } = values;
      const fatherLabel = isArabic ? "الأب" : t("ficheClasse.pere");
      const motherLabel = isArabic ? "الأم" : t("ficheClasse.mere");

      if (father && mother) {
        return `${fatherLabel} : ${father}${separator}${motherLabel} : ${mother}`;
      }

      return father || mother || "--";
    },

    formatParentSheetPhoneValue(
      values,
      t,
      { isArabic = false, isRtl = isArabic, separator = "\n", isExcel = false } = {}
    ) {
      if (!isRtl) {
        return this.formatParentSheetDualValue(values, t, { isArabic, separator });
      }

      const { father, mother } = values;
      const fatherLabel = isArabic ? "الأب" : t("ficheClasse.pere");
      const motherLabel = isArabic ? "الأم" : t("ficheClasse.mere");
      const ltr = (phone) => (isExcel ? `\u200E${phone}` : phone);

      if (father && mother) {
        if (isExcel) {
          return `${fatherLabel} : ${ltr(father)}${separator}${motherLabel} : ${ltr(mother)}`;
        }
        return `${father} : ${fatherLabel}${separator}${mother} : ${motherLabel}`;
      }

      const phone = father || mother;
      return phone ? ltr(phone) : "--";
    },

    getParentSheetColumnValue(
      student,
      columnKey,
      t,
      { isArabic = false, isRtl = isArabic, separator = "\n", isExcel = false } = {}
    ) {
      switch (columnKey) {
        case "fullName":
          return student.fullName || "--";
        case "fullNameFr":
          return student.fullNameFr || "--";
        case "uniqueId":
          return isKenyanSchool()
            ? student.nemisNumber || "--"
            : student.uniqueId || "--";
        case "birthDate":
          return student.BirthDate || "--";
        case "parentName":
          if (student.parent?.father && student.parent?.mother) {
            if (isArabic) {
              const formatted = this.formatParentNamesForArabic(
                student.parent.father,
                student.parent.mother
              );
              return separator !== "\n"
                ? formatted.replace(/\n/g, separator)
                : formatted;
            }
            return `${t("ficheClasse.pere")} : ${student.parent.father}${separator}${t("ficheClasse.mere")} : ${student.parent.mother}`;
          }
          return student.parent?.father || student.parent?.mother || "--";
        case "phone":
          return this.formatParentSheetPhoneValue(student.phone || {}, t, {
            isArabic,
            isRtl,
            separator,
            isExcel,
          });
        case "email":
          return this.formatParentSheetDualValue(student.email || {}, t, {
            isArabic,
            separator,
          });
        case "address":
          return student.adress || "--";
        default:
          return "--";
      }
    },

    buildParentSheetPdfTable(selectedColumns, t, { isArabic = false, isRtl = isArabic } = {}) {
      const headers = [
        isArabic ? "ر.ت" : "N°",
        ...selectedColumns.map((columnKey) =>
          this.getParentSheetColumnHeader(columnKey, t, isArabic)
        ),
      ];

      if (isArabic) {
        headers.reverse();
      }

      const rows = this.filtredTable.map((student, index) => {
        const cells = [
          index + 1,
          ...selectedColumns.map((columnKey) =>
            this.getParentSheetColumnValue(student, columnKey, t, { isArabic, isRtl })
          ),
        ];

        if (isArabic) {
          cells.reverse();
        }

        return cells;
      });

      return { headers, rows };
    },

    async generateFicheParentPDF(lang = null, selectedColumns = null) {
      const columns = selectedColumns || this.getDefaultParentSheetColumns();

      // Determine language: use parameter if provided, otherwise use locale or country code
      if (!lang) {
        lang = this.countryIsTN ? "fr" : this.$i18n.locale.split("-")[0];
      }

      const t = this.getParentSheetTranslationHelper(lang);
      const localeMap = {
        ar: "ar-AR",
        fr: "fr-FR",
        en: "en-US",
        sw: "sw-KE",
      };
      const targetLocale = localeMap[lang] || "fr-FR";

      // Refresh group assignments before generating PDF
      await this.fetchGroupAssignments();

      const doc = new JsPDF({ orientation: "portrait" });
      doc.setFont("Amiri", "normal");

      let imgData = null;
      let imgLoaded = false;

      if (this.settings.image && Array.isArray(this.settings.image)) {
        for (const img of this.settings.image) {
          if (this.settings.activeBuilding === img.db && img.logo) {
            try {
              imgData = new Image();
              imgData.src = img.logo;
              imgLoaded = true;
              break;
            } catch (error) {
              console.error("Error loading image:", error);
            }
          }
        }
      }

      // Calculate logo position to match left text margin
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const pageCenterX = pageWidth / 2;
      const leftMargin = 10;
      const rightMargin = 10;
      const logoWidth = 30;
      const logoHeight = 30;
      const logoX = pageWidth - leftMargin - logoWidth; // Same margin on right as left

      if (imgLoaded && imgData) {
        try {
          doc.addImage(imgData, "JPEG", logoX, 10, logoWidth, logoHeight);
        } catch (error) {
          // Continue with PDF generation even if image fails
        }
      }

      const schoolDetails = this.user.building.filter(
        (el) => el.dbName == this.settings.activeBuilding
      )[0];
      const delegation = schoolDetails?.delegeRegional || t("ficheClasse.nonDisponible");

      const { start, end } = this.settings.schoolarYears.find(
        (el) => el._id == this.settings.activeSchoolarYear
      );

      const startYear = new Date(start).getFullYear();
      const endYear = new Date(end).getFullYear();
      const schoolarYearName = `${startYear} / ${endYear}`;

      doc.setFontSize(12);
      // Center text vertically with logo (logo center is at y=10 + logoHeight/2 = 25)
      const logoCenterY = 10 + logoHeight / 2;
      const lineHeight = 10; // Approximate line height for font size 12

      doc.text(
        getFullSchoolName(schoolDetails, null, targetLocale),
        leftMargin,
        logoCenterY - lineHeight / 2,
        {
          align: "left",
        }
      );
      doc.text(
        `${t("ficheClasse.delegeRegionalEducation")}: ${delegation}`,
        leftMargin,
        logoCenterY + lineHeight / 2,
        {
          align: "left",
        }
      );

      doc.setFontSize(20);
      const formTitle = t("ficheClasse.formulaireParent");
      doc.text(formTitle, pageCenterX, 50, { align: "center" });

      doc.setFontSize(12);
      const className = this.classroom?.name || t("ficheClasse.nonDisponible");
      const classText = `${t("ficheClasse.classe")}: ${className}`;
      doc.text(classText, pageCenterX, 60, { align: "center" });

      const yearText = `${t("ficheClasse.anneeScolaireLabel")}: ${schoolarYearName}`;
      doc.text(yearText, pageCenterX, 70, { align: "center" });

      const { headers, rows } = this.buildParentSheetPdfTable(columns, t, {
        isRtl: lang === "ar",
      });
      const usableWidth = pageWidth - leftMargin - rightMargin;
      const numeroColWidth = 8;
      const columnWeights = {
        fullName: 1.4,
        fullNameFr: 1.4,
        uniqueId: 1,
        birthDate: 1.2,
        parentName: 1.7,
        phone: 1.5,
        email: 1.5,
        address: 1.5,
      };
      const totalWeight = columns.reduce(
        (total, columnKey) => total + (columnWeights[columnKey] || 1),
        0
      );
      const dataWidth = usableWidth - numeroColWidth;
      const columnStyles = {
        0: { cellWidth: numeroColWidth, halign: "center" },
      };
      columns.forEach((columnKey, index) => {
        columnStyles[index + 1] = {
          cellWidth:
            dataWidth * ((columnWeights[columnKey] || 1) / totalWeight),
        };
      });

      doc.autoTable({
        theme: "grid",
        styles: {
          font: "Amiri",
          halign: "left",
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.5,
          fontSize: columns.length > 5 ? 8 : 10,
          cellPadding: 1.5,
          overflow: "linebreak",
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          lineWidth: 0.5,
          lineColor: [0, 0, 0],
          fontStyle: "bold",
        },
        bodyStyles: {
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.5,
        },
        columnStyles,
        tableWidth: usableWidth,
        head: [headers],
        body: rows,
        startY: 80,
        margin: { left: leftMargin, right: rightMargin },
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.text(`${i} `, pageCenterX, pageHeight - 7, { align: "center" });
      }

      const pdfFileName = t("ficheClasse.ficheParentPDF");
      doc.save(`${pdfFileName}.pdf`);
    },

    async generateFicheParentPDFar(selectedColumns = null) {
  const columns = selectedColumns || this.getDefaultParentSheetColumns();
  await this.fetchGroupAssignments();

  const doc = new JsPDF({ orientation: "portrait" });
  doc.setFont("Amiri", "normal");

  let imgData = null;
  let imgLoaded = false;

  if (this.settings.image && Array.isArray(this.settings.image)) {
    for (const img of this.settings.image) {
      if (this.settings.activeBuilding === img.db && img.logo) {
        try {
          imgData = new Image();
          imgData.src = img.logo;
          imgLoaded = true;
          break;
        } catch (error) {
          console.error("Error loading image:", error);
        }
      }
    }
  }

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageCenterX = pageWidth / 2;
  const leftMargin = 10;
  const rightMargin = 10;
  const logoWidth = 30;
  const logoHeight = 30;
  const logoX = leftMargin;
  const textX = pageWidth - leftMargin;

  if (imgLoaded && imgData) {
    try {
      doc.addImage(imgData, "JPEG", logoX, 10, logoWidth, logoHeight);
    } catch (error) {
      // Continue with PDF generation even if image fails
    }
  }

  const schoolDetails = this.user.building.filter(
    (el) => el.dbName == this.settings.activeBuilding
  )[0];
  const delegation = schoolDetails?.delegeRegional || "غير متوفر";

  const { start, end } = this.settings.schoolarYears.find(
    (el) => el._id == this.settings.activeSchoolarYear
  );

  const startYear = new Date(start).getFullYear();
  const endYear = new Date(end).getFullYear();
  const schoolarYearName = `${endYear} / ${startYear}`;

  doc.setFontSize(12);
  const logoCenterY = 10 + logoHeight / 2;
  const lineHeight = 10;

  doc.text(
    getFullSchoolName(schoolDetails, null, "ar-AR"),
    textX,
    logoCenterY - lineHeight / 2,
    { align: "right" }
  );
  doc.text(
    `المندوبية الجهوية للتربية: ${delegation}`,
    textX,
    logoCenterY + lineHeight / 2,
    { align: "right" }
  );

  doc.setFontSize(20);
  doc.text("استمارة ولي الأمر", pageCenterX, 50, { align: "center" });

  doc.setFontSize(12);
  const className = this.classroom?.name || "غير متوفر";
  const hasArabicText = /[\u0600-\u06FF]/.test(className);
  const classText = hasArabicText
    ? `القسم : ${className}`
    : `${className} : القسم`;
  doc.text(classText, pageCenterX, 60, { align: "center" });

  const yearText = `السنة: ${schoolarYearName}`;
  doc.text(yearText, pageCenterX, 70, { align: "center" });

  const { headers, rows } = this.buildParentSheetPdfTable(columns, () => "", {
    isArabic: true,
  });

  // Numero column sits last after Arabic header reversal — pin it narrow
  const numeroColIndex = columns.length;
  const usableWidth = pageWidth - leftMargin - rightMargin;
  const numeroColWidth = 8;
  const remainingWidth = usableWidth - numeroColWidth;
  const dataColWidth = remainingWidth / columns.length;

  // Shrink font as more columns are packed in, so text still fits per cell
  const fontSize =
    columns.length <= 3 ? 10 :
    columns.length <= 5 ? 8.5 :
    7;

  const columnStyles = { [numeroColIndex]: { cellWidth: numeroColWidth, halign: "center" } };
  for (let i = 0; i < columns.length; i++) {
    columnStyles[i] = { cellWidth: dataColWidth };
  }

  doc.autoTable({
    theme: "grid",
    styles: {
      font: "Amiri",
      halign: "right",
      textColor: [0, 0, 0],
      lineColor: [0, 0, 0],
      lineWidth: 0.5,
      fontSize,
      cellPadding: 1.5,
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      lineWidth: 0.5,
      lineColor: [0, 0, 0],
      fontStyle: "bold",
      fontSize,
    },
    bodyStyles: {
      textColor: [0, 0, 0],
      lineColor: [0, 0, 0],
      lineWidth: 0.5,
    },
    columnStyles,
    tableWidth: usableWidth,
    head: [headers],
    body: rows,
    startY: 80,
    margin: { left: leftMargin, right: rightMargin },
  });

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.text(`${i} `, pageCenterX, 290, { align: "center" });
  }

  doc.save("بطاقة_الوالدين.pdf");
},

    async generateFicheParentExcel(lang = null, selectedColumns = null) {
      const columns = selectedColumns || this.getDefaultParentSheetColumns();

      // Determine language: use parameter if provided, otherwise use locale or country code
      if (!lang) {
        lang = this.countryIsTN ? "fr" : this.$i18n.locale.split("-")[0];
      }

      const t = this.getParentSheetTranslationHelper(lang);

      // Refresh group assignments before generating Excel
      await this.fetchGroupAssignments();

      const isArabic = lang === "ar";
      const separator = ", ";

      const excelData = [
        columns.map((columnKey) =>
          this.getParentSheetColumnHeader(columnKey, t, isArabic)
        ),
      ];

      this.filtredTable.forEach((student) => {
        excelData.push(
          columns.map((columnKey) =>
            this.getParentSheetColumnValue(student, columnKey, t, {
              isArabic,
              separator,
              isExcel: true,
            })
          )
        );
      });

      const columnWidths = {
        fullName: 30,
        fullNameFr: 30,
        uniqueId: 20,
        birthDate: 15,
        parentName: 40,
        phone: 30,
        email: 35,
        address: 35,
      };

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(excelData);
      ws["!cols"] = columns.map((columnKey, colIndex) => {
        const longestValue = Math.max(
          ...excelData.map((row) => String(row[colIndex] ?? "").length)
        );
        return {
          wch: Math.max(columnWidths[columnKey] || 20, longestValue + 2),
        };
      });

      const sheetName = t("ficheClasse.formulaireParent");
      const fileName = `${t("ficheClasse.ficheParentPDF")}.xlsx`;
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      XLSX.writeFile(wb, fileName);
    },

    editScolariteFees(record) {
      // Get the student's scolarité payment data
      this.getStudentScolariteData(record._id).then((scolariteData) => {
        if (scolariteData) {
          this.selectedScolarite = scolariteData;
          this.editModalVisible = true;
        } else {
          this.$message.warning(this.$t("paiement.aucunPaiementScolarite"));
        }
      });
    },
    async getStudentScolariteData(studentId) {
      try {
        let response = await apiClient.get(
          `/payment/manager/pvalidator/${studentId}`
        );
        response = response.data.filter((p) => p.typeAS == "S");
        response = response[0] || [];
        return response;
      } catch (error) {
        console.error("Error fetching scolarité data:", error);
        return null;
      }
    },
    closeEditModal() {
      this.editModalVisible = false;
      this.selectedScolarite = null;
    },
  },
};
</script>
<style scoped>
.custom-border {
  border: 1px solid #6c757d;
  border-radius: 0.25rem;
  cursor: pointer;
}
.custom-border-active {
  border: 3px solid #22bb33;
  border-radius: 0.25rem;
  cursor: pointer;
}
.month-discount-inline {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  min-height: 32px;
}
.fee-month-header {
  margin-bottom: 4px;
}
.fee-month-header-label {
  color: rgba(0, 0, 0, 0.85);
  font-size: 14px;
  line-height: 1.5;
  padding-bottom: 4px;
}
</style>
<style>
.enrollment-availability-btn,
.enrollment-availability-btn:hover,
.enrollment-availability-btn:focus,
.enrollment-availability-btn:active {
  background-color: #52c41a !important;
  border-color: #52c41a !important;
  color: #fff !important;
}
</style>

